import type { RioComponent, RioRule } from '@backoffice-frontend/graphql'
import {
  RioComponentType,
  RioRuleDefinition,
} from '@backoffice-frontend/graphql'
import type { RioTemplateFragment } from '@backoffice-frontend/report-io-lib'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { Control, FieldValues } from 'react-hook-form'
import { type DeepPartialSkipArrayKey, useWatch } from 'react-hook-form'

type ReportIoRulesContextProviderProps = {
  children: ReactNode
  template: RioTemplateFragment
  control: Control<FieldValues, string>
}

export type RulesContextProps = {
  rules: ReportIoRule[]
}

const RulesContext = createContext<RulesContextProps>({
  rules: [],
})

export type ReportIoRule = RioRule & {
  preventStoreReport: boolean
  active: boolean
}

export const isValueSet = (
  componentValues: DeepPartialSkipArrayKey<FieldValues>,
  component: RioComponent,
): boolean => {
  if (component.componentType === RioComponentType.Checkbox) {
    return componentValues[component.componentId] === 'true'
  }
  return (
    componentValues[component.componentId] !== '' &&
    componentValues[component.componentId] !== undefined
  )
}

export const mandatoryRulesValidator = (
  template: RioTemplateFragment,
  componentValues: DeepPartialSkipArrayKey<FieldValues>,
  rules: ReportIoRule[],
) => {
  template.rules
    ?.filter(rule => rule.rule === RioRuleDefinition.Mandatory)
    .forEach(rule => {
      if (!rule.componentId) return
      const component = template.components.find(
        component => component.componentId === rule.componentId,
      )!

      const preventStoreReport = !isValueSet(componentValues, component)
      rules.push({
        ...rule,
        preventStoreReport: preventStoreReport,
        active: true,
      })
    })
}

export const mandatoryOnValueRulesValidator = (
  template: RioTemplateFragment,
  componentValues: DeepPartialSkipArrayKey<FieldValues>,
  rules: ReportIoRule[],
) => {
  template.rules
    ?.filter(rule => rule.rule === RioRuleDefinition.MandatoryOnValue)
    .forEach(rule => {
      const otherComponentValue = componentValues[rule.otherComponentId!]
      if (!rule.componentId) return
      const component = template.components.find(
        component => component.componentId === rule.componentId,
      )!

      const active = otherComponentValue === rule.otherComponentValue
      const preventStoreReport =
        active && !isValueSet(componentValues, component)

      rules.push({
        ...rule,
        preventStoreReport: preventStoreReport,
        active,
      })
    })
}

export const visibleOnValueRulesValidator = (
  template: RioTemplateFragment,
  componentValues: DeepPartialSkipArrayKey<FieldValues>,
  rules: ReportIoRule[],
) => {
  template.rules
    ?.filter(rule => rule.rule === RioRuleDefinition.VisibleOnValue)
    .forEach(rule => {
      const otherComponentValue = componentValues[rule.otherComponentId!]
      if (!rule.componentId) return

      const active = otherComponentValue === rule.otherComponentValue
      rules.push({
        ...rule,
        preventStoreReport: false,
        active,
      })
    })
}

const getRulesValue = (
  template: RioTemplateFragment,
  componentValues: DeepPartialSkipArrayKey<FieldValues>,
) => {
  const rules: ReportIoRule[] = []
  mandatoryRulesValidator(template, componentValues, rules)
  mandatoryOnValueRulesValidator(template, componentValues, rules)
  visibleOnValueRulesValidator(template, componentValues, rules)
  return rules
}

export const ReportIoRulesContextProvider = ({
  children,
  template,
  control,
}: ReportIoRulesContextProviderProps) => {
  const componentValues = useWatch({ control: control })

  const [rules, setRulesValue] = useState<ReportIoRule[]>(
    getRulesValue(template, componentValues),
  )

  const prevComponentValues = useRef(componentValues)

  const valuesChanged = (
    newValues: { [key: string]: string },
    oldValues: { [key: string]: string },
  ) => {
    const newKeys = Object.keys(newValues)
    const oldKeys = Object.keys(oldValues)

    if (newKeys.length !== oldKeys.length) {
      return true
    }

    for (const key of newKeys) {
      if (newValues[key] !== oldValues[key]) {
        return true
      }
    }

    return false
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <auto ignored after biome migration>
  useEffect(() => {
    if (valuesChanged(componentValues, prevComponentValues.current)) {
      prevComponentValues.current = componentValues
      setRulesValue(getRulesValue(template, componentValues))
    }
  }, [componentValues, template.rules, template, setRulesValue])

  return (
    <RulesContext.Provider value={{ rules }}>{children}</RulesContext.Provider>
  )
}

export const useReportIoRulesContext = () => useContext(RulesContext)
